import React, { useEffect, useState } from "react";
import { Box, Button, TextField, MenuItem, Typography, Snackbar, Alert } from "@mui/material";
import { AiOutlineEdit, AiOutlineUser, AiOutlineShareAlt } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import PageSpinner from "../../components/common/PageSpinner/PageSpinner";
import FallbackImage from "../../components/common/FallbackImage/FallbackImage";
import { apiFetch, API_BASE } from "../../utils/api";
import * as Sentry from "@sentry/react";
import styles from "./ProfileSetting.module.css";

const ProfileSetting = () => {
  const [profile, setProfile] = useState({
    name: "",
    surname: "",
    email: "",
    type: "",
    address: "",
    zip: "",
    country: "",
    language: "",
    twitter: "",
    instagram: "",
    telegram: "",
    tiktok: "",
    background_image: "",
    icon_image: "",
    description: "",
    descriptor: "",
  });
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [bgFile, setBgFile] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [bgPreview, setBgPreview] = useState("/bg@2x.png");
  const [iconPreview, setIconPreview] = useState("/img@2x.png");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusSeverity, setStatusSeverity] = useState("success");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsPageLoading(true);
        const data = await apiFetch("/api/profile", { method: "GET" }, true);
        setProfile(data);
        if (data.background_image) setBgPreview(`${API_BASE}${data.background_image}`);
        if (data.icon_image) setIconPreview(`${API_BASE}${data.icon_image}`);
      } catch (err) {
        Sentry.captureException(err);
        console.error("Failed to fetch profile", err);
      } finally {
        setIsPageLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleFileUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadResponse = await fetch(`${API_BASE}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        body: formData
      });
  
      if (!uploadResponse.ok) {
        setStatusMessage("❌ Upload failed.");
        setStatusSeverity("error");
        setSnackbarOpen(true);
        return null;
      }
  
      const uploadResult = await uploadResponse.json();
      return `/${uploadResult.url}` || null;
    } catch (err) {
      Sentry.captureException(err);
      console.error("❌ File upload error:", err);
      setStatusMessage("❌ Upload error.");
      setStatusSeverity("error");
      setSnackbarOpen(true);
      return null;
    }
  };

  const resizeImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.8) => {

    console.log("resizing")
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = function (event) {
        const img = new Image();
        img.onload = function () {
          let width = img.width;
          let height = img.height;
  
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height = height * (maxWidth / width);
              width = maxWidth;
            } else {
              width = width * (maxHeight / height);
              height = maxHeight;
            }
          }
  
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
  
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const resizedFile = new File([blob], file.name, {
                  type: "image/jpeg",
                  lastModified: Date.now(),
                });
                resolve(resizedFile);
              } else {
                reject(new Error("Canvas toBlob failed"));
              }
            },
            "image/jpeg",
            quality
          );
        };
        img.onerror = reject;
        img.src = event.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleBgChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    let ext = file.name?.split(".").pop()?.toLowerCase() || "";
    let type = file.type || "";
  
    if (!type && !ext) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (result?.startsWith("data:image/")) {
          type = "image/jpeg";
          ext = "jpg";
          finalizeBg(file);
        } else {
          Sentry.captureMessage("❌ Unknown background file type");
          alert("Unsupported file format.");
        }
      };
      reader.readAsDataURL(file);
      return;
    }
  
    let finalFile = file;
  
    if (["image/jpeg", "image/png"].includes(finalFile.type) && finalFile.size > 5 * 1024 * 1024) {
      try {
        finalFile = await resizeImage(finalFile);
      } catch (err) {
        Sentry.captureException(err);
        alert("Image resize failed.");
        return;
      }
    }
  
    finalizeBg(finalFile);
  };
  
  const handleIconChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    let ext = file.name?.split(".").pop()?.toLowerCase() || "";
    let type = file.type || "";
  
    if (!type && !ext) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (result?.startsWith("data:image/")) {
          type = "image/jpeg";
          ext = "jpg";
          finalizeIcon(file);
        } else {
          Sentry.captureMessage("❌ Unknown icon file type");
          alert("Unsupported file format.");
        }
      };
      reader.readAsDataURL(file);
      return;
    }
  
    let finalFile = file;
  
    if (["image/jpeg", "image/png"].includes(finalFile.type) && finalFile.size > 5 * 1024 * 1024) {
      try {
        finalFile = await resizeImage(finalFile);
      } catch (err) {
        Sentry.captureException(err);
        alert("Image resize failed.");
        return;
      }
    }
  
    finalizeIcon(finalFile);
  };

  const finalizeBg = (file) => {
    setBgFile(file);
    setBgPreview(URL.createObjectURL(file));
  };
  
  const finalizeIcon = (file) => {
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const handleUpdate = async () => {
    try {
      let updated = { ...profile };
      if (bgFile) {
        const bgUploaded = await handleFileUpload(bgFile);
        if (!bgUploaded) throw new Error("Failed to upload background image");
        updated.background_image = bgUploaded;
      }
  
      if (iconFile) {
        const iconUploaded = await handleFileUpload(iconFile);
        if (!iconUploaded) throw new Error("Failed to upload icon image");
        updated.icon_image = iconUploaded;
      }
  
      await apiFetch("/api/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updated),
      }, true);
  
      setStatusMessage("✅ Profile updated successfully.");
      setStatusSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      Sentry.captureException(err);
      console.error("❌ Fail", err);
      setStatusMessage(`❌ ${err}`);
      setStatusSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleChange = (field) => (e) => {
    setProfile({ ...profile, [field]: e.target.value });
  };

  const handleCheckPage = () => {
    if (profile.descriptor) {
      navigate(`/creator/${profile.descriptor}`);
    }
  };

  const handleSharePage = async () => {
    if (profile.descriptor) {
      const url = `${window.location.origin}/creator/${profile.descriptor}`;
      try {
        await navigator.clipboard.writeText(url);
        setStatusMessage("Personal page URL copied!");
        setStatusSeverity("success");
        setSnackbarOpen(true);
      } catch (err) {
        console.error("Failed to copy URL:", err);
      }
    }
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  if (isPageLoading) {
    return <PageSpinner />;
  }

  return (
    <Box className={styles.profileSetting}>

      {/* New Buttons */}
      <Box className={styles.extraButtons}>
        <Button onClick={handleCheckPage} className={styles.iconUploadButton}>
          <span className={styles.iconCircle}><AiOutlineUser /></span>
          <Box className={styles.buttonText}>Check Personal Page</Box>
        </Button>

        <Button onClick={handleSharePage} className={styles.iconUploadButton}>
          <span className={styles.iconCircle}><AiOutlineShareAlt /></span>
          <Box className={styles.buttonText}>Share Personal Page</Box>
        </Button>
      </Box>
      
      <Box className={styles.header}>
        <FallbackImage key={bgPreview}  className={styles.bgIcon} alt="Background" src={bgPreview} type={'image'} />

        <Box className={styles.headerButtons}>
          <Button component="label" className={styles.iconUploadButton}>
            <span className={styles.iconCircle}><AiOutlineEdit /></span>
            <Box className={styles.buttonText}>Background</Box>
            <input type="file" hidden onChange={handleBgChange} />
          </Button>

          <Button component="label" className={styles.iconUploadButton}>
            <span className={styles.iconCircle}><AiOutlineEdit /></span>
            <Box className={styles.buttonText}>Icon</Box>
            <input type="file" hidden onChange={handleIconChange} />
          </Button>
        </Box>

        <Box className={styles.iconContainer}>
          <FallbackImage key={iconPreview} className={styles.iconImage} alt="Icon" src={iconPreview} type={'user'} />
        </Box>


      </Box>



      {/* Form */}
      <Box className={styles.form}>
        <Box className={styles.column}>
          <Typography variant="h6">Personal Information</Typography>
          <TextField label="Name" fullWidth variant="standard" value={profile.name} onChange={handleChange("name")} />
          <TextField label="Surname" fullWidth variant="standard" value={profile.surname} onChange={handleChange("surname")} />
          <TextField label="Email" fullWidth variant="standard" value={profile.email} onChange={handleChange("email")} />
          <TextField select label="Type" fullWidth variant="standard" value={profile.type} onChange={handleChange("type")}>
            <MenuItem value="Athlete">Athlete</MenuItem>
            <MenuItem value="Artist">Artist</MenuItem>
            <MenuItem value="Organisation">Organisation</MenuItem>
            <MenuItem value="Team">Team</MenuItem>
            <MenuItem value="Event">Event</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>
        </Box>

        <Box className={styles.column}>
          <Typography variant="h6">Contact Information</Typography>
          <TextField label="Address" fullWidth variant="standard" value={profile.address} onChange={handleChange("address")} />
          <TextField label="Zip" fullWidth variant="standard" value={profile.zip} onChange={handleChange("zip")} />
          <TextField label="Country" fullWidth variant="standard" value={profile.country} onChange={handleChange("country")} />
          <TextField label="Language" fullWidth variant="standard" value={profile.language} onChange={handleChange("language")} />
        </Box>

        <Box className={styles.column}>
          <Typography variant="h6">Social Media Links</Typography>
          <TextField label="Twitter Link" fullWidth variant="standard" value={profile.twitter} onChange={handleChange("twitter")} />
          <TextField label="Instagram Link" fullWidth variant="standard" value={profile.instagram} onChange={handleChange("instagram")} />
          <TextField label="Telegram Link" fullWidth variant="standard" value={profile.telegram} onChange={handleChange("telegram")} />
          <TextField label="TikTok Link" fullWidth variant="standard" value={profile.tiktok} onChange={handleChange("tiktok")} />
        </Box>
      </Box>

      <Box className={styles.description}>
        <Typography variant="h6">About you:</Typography>
        <TextField 
          label="About you" 
          fullWidth 
          multiline
          rows={8}
          variant="standard" 
          value={profile.description} 
          onChange={handleChange("description")} />
      </Box>

      <Button className={styles.updateButton} onClick={handleUpdate}>
        Update Settings
      </Button>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={statusSeverity} onClose={handleSnackbarClose}>
          {statusMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfileSetting;